import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';

const VDR_DRAG_TYPE = 'application/x-vdr-item';
const VDR_FILE_DRAG_TYPE = 'application/x-vdr-file';

interface FileAccess {
  view: boolean;
  download: boolean;
}

interface File {
  fileId: string;
  fileName: string;
  fileURL: string;
  fileAccess: FileAccess;
  folderId?: string;
  folderName?: string;
}

interface Props {
  folderPath: string[];
  folderId: string;
  files: File[];
  onEdit: (file: string) => void;
  onDelete: (file: string) => void;
  onView: (file: string) => void;
  onDownload: (file: string) => void;
  onPermissions: (file: string) => void;
  onDrop: (files: FileList) => void;
  accessType: 'ADMIN' | 'MEMBER';
}

const createDragPreview = (label: string) => {
  const preview = document.createElement('div');

  preview.style.position = 'fixed';
  preview.style.top = '-1000px';
  preview.style.left = '-1000px';
  preview.style.display = 'flex';
  preview.style.alignItems = 'center';
  preview.style.gap = '8px';
  preview.style.maxWidth = '320px';
  preview.style.padding = '8px 12px';
  preview.style.background = '#ffffff';
  preview.style.border = '1px solid rgba(0, 0, 0, 0.18)';
  preview.style.borderRadius = '6px';
  preview.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.18)';
  preview.style.fontFamily = 'Barlow, sans-serif';
  preview.style.fontSize = '14px';
  preview.style.color = '#333333';
  preview.style.whiteSpace = 'nowrap';
  preview.style.overflow = 'hidden';
  preview.style.textOverflow = 'ellipsis';
  preview.style.pointerEvents = 'none';
  preview.style.zIndex = '99999';

  const icon = document.createElement('span');
  icon.textContent = '▣';
  icon.style.color = '#777777';
  icon.style.fontSize = '16px';
  icon.style.lineHeight = '1';

  const text = document.createElement('span');
  text.textContent = label;
  text.style.overflow = 'hidden';
  text.style.textOverflow = 'ellipsis';

  preview.appendChild(icon);
  preview.appendChild(text);
  document.body.appendChild(preview);

  return preview;
};

const FileViewer: React.FC<Props> = ({
  folderPath,
  folderId,
  files,
  onEdit,
  onDelete,
  onView,
  onDownload,
  onPermissions,
  onDrop,
  accessType
}) => {

  const filteredFiles = files;
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  const isInternalDrag = (types: readonly string[] | DOMStringList) =>
    Array.from(types).includes(VDR_DRAG_TYPE);

  return (
    <Box
      onDrop={(e) => {
        e.preventDefault();

        if (isInternalDrag(e.dataTransfer.types)) {
          return;
        }

        if (accessType === 'ADMIN' && e.dataTransfer.files.length > 0) {
          onDrop(e.dataTransfer.files);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();

        if (isInternalDrag(e.dataTransfer.types)) {
          e.dataTransfer.dropEffect = 'none';
        } else if (accessType === 'ADMIN') {
          e.dataTransfer.dropEffect = 'copy';
        }
      }}
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 2,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <InsertDriveFileIcon sx={{ mr: 1, color: '#73AF73' }} />
        <Typography variant="h6" fontWeight="bold" sx={{ color: '#73AF73' }}>
          {t('filesIn')} "{folderPath.join(' / ')}"
        </Typography>
      </Box>

      {accessType !== 'ADMIN' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dragDrop')}
        </Typography>
      )}

      <List sx={{ flexGrow: 1 }}>
        {filteredFiles.map((file) => {
          const sourceFolderId = file.folderId || folderId;

          return (
            <ListItem
              key={file.fileId}
              draggable={accessType === 'ADMIN' && sourceFolderId !== 'search'}
              onDragStart={(e) => {
                if (accessType !== 'ADMIN' || sourceFolderId === 'search') {
                  e.preventDefault();
                  return;
                }

                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(
                  VDR_DRAG_TYPE,
                  JSON.stringify({
                    type: 'file',
                    fileId: file.fileId,
                    fileName: file.fileName,
                    sourceFolderId,
                  })
                );
                e.dataTransfer.setData(VDR_FILE_DRAG_TYPE, '1');
                e.dataTransfer.setData('text/plain', file.fileName);

                const preview = createDragPreview(file.fileName);
                e.dataTransfer.setDragImage(preview, 18, 18);

                window.setTimeout(() => {
                  preview.remove();
                }, 0);
              }}
              onDoubleClick={() => onView(file.fileName)}
              onClick={() => onView(file.fileName)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px dotted #ccc',
                '&:last-child': { borderBottom: 'none' },
                cursor: accessType === 'ADMIN' ? 'grab' : 'pointer',
                userSelect: 'none',
                '&:active': {
                  cursor: accessType === 'ADMIN' ? 'grabbing' : 'pointer',
                },
              }}
            >
              <ListItemIcon>
                <InsertDriveFileIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.fileName}
                secondary={file.folderName ? `Folder: ${file.folderName}` : undefined}
              />
              <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={(e) => { e.stopPropagation(); onView(file.fileName); }}
                  sx={{
                    visibility: accessType === 'ADMIN' || file.fileAccess?.view ? 'visible' : 'hidden'
                  }}
                >
                  <VisibilityIcon />
                </IconButton>

                <IconButton
                  onClick={(e) => { e.stopPropagation(); onDownload(file.fileName); }}
                  sx={{
                    visibility: accessType === 'ADMIN' || file.fileAccess?.download ? 'visible' : 'hidden'
                  }}
                >
                  <DownloadIcon />
                </IconButton>

                {accessType === 'ADMIN' && (
                  <>
                    <IconButton onClick={(e) => { e.stopPropagation(); onPermissions(file.fileName); }}>
                      <LockOpenIcon />
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); onEdit(file.fileName); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={(e) => { e.stopPropagation(); onDelete(file.fileName); }}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default FileViewer;
